import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileText, IndianRupee, Calendar, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pager from "@/components/site/Pager";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["offer_extended", "offer_accepted", "joined", "no_show", "replaced"];
const INVOICE_STATUSES = ["not_raised", "draft", "sent", "paid", "overdue"];
const STATUS_COLORS = {
  offer_extended: "bg-amber-100 text-amber-700",
  offer_accepted: "bg-blue-100 text-blue-700",
  joined: "bg-emerald-100 text-emerald-700",
  no_show: "bg-rose-100 text-rose-700",
  replaced: "bg-slate-100 text-slate-600",
};
const PAGE_SIZE = 20;

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminPlacements() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total: 0, total_fee: 0 });
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/placements", { params: { status: statusF || undefined, page, page_size: PAGE_SIZE } })
    .then((r) => {
      const data = r?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotals({ total: data.total || 0, total_fee: data.total_fee || 0 });
    })
    .catch(() => {
      // Keep previous items rather than blanking the page on transient failure
      toast.error("Failed to reload placements");
    });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, page]);
  useEffect(() => { setPage(1); }, [statusF]);

  const raiseInvoice = async (pid) => {
    try { const r = await api.post(`/admin/invoices/from-placement/${pid}`); toast.success(`Invoice ${r.data.invoice_number} created`); load(); }
    catch { toast.error("Failed to raise invoice"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this placement?")) return;
    await api.delete(`/admin/placements/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-placements">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Placements & Offers</h1>
          <p className="text-sm text-slate-500">{totals.total} records · Fees booked: <span className="font-semibold text-primary">{inr(totals.total_fee)}</span></p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="placement-new"><Plus className="mr-1 h-4 w-4" />New placement</Button>
      </div>

      <div className="flex items-center gap-2">
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="placements-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Candidate</th>
              <th className="px-4 py-3">Client / Role</th>
              <th className="px-4 py-3">CTC / Fee</th>
              <th className="px-4 py-3">Joining</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`placement-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{p.candidate_name}</div>
                  <div className="text-xs text-slate-500">{p.candidate_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{p.client_name || "—"}</div>
                  <div className="text-xs text-slate-500">{p.job_title}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 font-medium text-slate-900"><IndianRupee className="h-3 w-3" />{(p.ctc_offered || 0).toLocaleString("en-IN")}</div>
                  <div className="text-xs text-secondary">Fee: {inr(p.fee_amount)} ({p.fee_percent}%)</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 text-slate-700"><Calendar className="h-3 w-3" />{p.joining_date || "—"}</div>
                  {p.guarantee_end && <div className="flex items-center gap-1 text-xs text-emerald-600"><ShieldCheck className="h-3 w-3" />Guarantee → {p.guarantee_end}</div>}
                </td>
                <td className="px-4 py-3"><Badge className={STATUS_COLORS[p.status]}>{p.status.replace("_", " ")}</Badge></td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="capitalize">{p.invoice_status?.replace("_", " ")}</Badge>
                  {p.invoice_status === "not_raised" && (
                    <Button variant="link" size="sm" onClick={() => raiseInvoice(p.id)} className="ml-1 h-auto p-0 text-xs" data-testid={`placement-raise-invoice-${p.id}`}>Raise</Button>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Link to="/admin/invoices"><Button variant="ghost" size="sm"><FileText className="h-3.5 w-3.5" /></Button></Link>
                  <Button variant="ghost" size="sm" onClick={() => remove(p.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No placements yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} pageSize={PAGE_SIZE} total={totals.total} onChange={setPage} testIdPrefix="admin-placements-pager" />

      {creating && <PlacementDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <PlacementDialog value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PlacementDialog({ value, onClose, onSaved }) {
  const isEdit = !!value?.id;
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [f, setF] = useState(value || {
    application_id: "", client_id: "", client_name: "", recruiter_id: "", recruiter_name: "",
    ctc_offered: 0, ctc_fixed: 0, ctc_variable: 0, joining_date: "", notice_period: "",
    fee_percent: 8.33, guarantee_days: 90, status: "offer_extended", notes: "",
  });
  useEffect(() => {
    if (!isEdit) api.get("/admin/applications", { params: { page_size: 200 } }).then((r) => setApps(r.data.items || []));
    api.get("/admin/clients").then((r) => setClients(r.data.items || []));
    api.get("/admin/recruiters").then((r) => setRecruiters(r.data.items || []));
  }, [isEdit]);
  const save = async () => {
    // Validation: application_id is required by the backend on create.
    if (!isEdit && !f.application_id) {
      toast.error("Please select a candidate before saving");
      return;
    }
    try {
      if (isEdit) await api.put(`/admin/placements/${value.id}`, f);
      else await api.post("/admin/placements", f);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(" ") : (detail || e.message || "Save failed");
      toast.error(msg);
    }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Update placement — ${value.candidate_name}` : "New placement"}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">A placement records a candidate who has accepted an offer / joined a client. Fill in whatever&apos;s known — you can edit later.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {!isEdit && (
            <FormField className="sm:col-span-2" label="Candidate *" helper="Pick the application this placement belongs to.">
              <Select value={f.application_id} onValueChange={(v) => { const a = apps.find((x) => x.id === v); setF({ ...f, application_id: v, candidate_name: a?.candidate_name || "", candidate_email: a?.email || "", job_id: a?.job_id || "", job_title: a?.job_title || "" }); }}>
                <SelectTrigger data-testid="placement-candidate"><SelectValue placeholder="Select a candidate application" /></SelectTrigger>
                <SelectContent className="max-h-80">{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.candidate_name} — {a.job_title || "General"}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
          )}
          <FormField label="Client company" helper="Which client is this placement with?">
            <Select value={f.client_id || ""} onValueChange={(v) => { const c = clients.find((x) => x.id === v); setF({ ...f, client_id: v, client_name: c?.name || "", fee_percent: c?.fee_percent ?? f.fee_percent }); }}>
              <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Recruiter" helper="Internal recruiter who owned this mandate.">
            <Select value={f.recruiter_id || ""} onValueChange={(v) => { const r = recruiters.find((x) => x.id === v); setF({ ...f, recruiter_id: v, recruiter_name: r?.name || "" }); }}>
              <SelectTrigger><SelectValue placeholder="Assign recruiter" /></SelectTrigger>
              <SelectContent>{recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="CTC offered (₹)" helper="Total annual CTC in Indian Rupees.">
            <Input type="number" placeholder="e.g. 1200000" value={f.ctc_offered} onChange={(e) => setF({ ...f, ctc_offered: parseFloat(e.target.value) || 0 })} data-testid="placement-ctc" />
          </FormField>
          <FormField label="Fee %" helper="Percentage of CTC billed to the client (defaults to client's contracted %).">
            <Input type="number" step="0.01" placeholder="e.g. 8.33" value={f.fee_percent} onChange={(e) => setF({ ...f, fee_percent: parseFloat(e.target.value) || 0 })} />
          </FormField>
          <FormField label="Fixed pay (₹)" helper="Fixed component of the offered CTC.">
            <Input type="number" placeholder="Fixed pay component" value={f.ctc_fixed} onChange={(e) => setF({ ...f, ctc_fixed: parseFloat(e.target.value) || 0 })} />
          </FormField>
          <FormField label="Variable pay (₹)" helper="Bonus / variable component of the CTC.">
            <Input type="number" placeholder="Variable component" value={f.ctc_variable} onChange={(e) => setF({ ...f, ctc_variable: parseFloat(e.target.value) || 0 })} />
          </FormField>
          <FormField label="Joining date" helper="Expected or actual date of joining.">
            <Input type="date" value={f.joining_date || ""} onChange={(e) => setF({ ...f, joining_date: e.target.value })} data-testid="placement-joining" />
          </FormField>
          <FormField label="Notice period" helper="Notice the candidate must serve before joining.">
            <Input placeholder="e.g. 30 days, 2 months" value={f.notice_period} onChange={(e) => setF({ ...f, notice_period: e.target.value })} />
          </FormField>
          <FormField label="Guarantee (days)" helper="Free-replacement window if candidate leaves early.">
            <Input type="number" placeholder="e.g. 90" value={f.guarantee_days} onChange={(e) => setF({ ...f, guarantee_days: parseInt(e.target.value) || 0 })} />
          </FormField>
          <FormField label="Placement status" helper="Current stage of this placement.">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="placement-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          {isEdit && (
            <FormField label="Invoice status" helper="Where the invoice for this placement stands.">
              <Select value={f.invoice_status || "not_raised"} onValueChange={(v) => setF({ ...f, invoice_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
          )}
          <FormField className="sm:col-span-2" label="Notes" helper="Internal notes — visible only in the admin console.">
            <Textarea placeholder="Any internal notes about this placement…" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="placement-save">Save placement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, helper, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}
